import dayjs from 'dayjs';

import { instanceCountFromValues } from './sectionInstances';
import type { FileEntryCondition } from './templateTypes';
import type { FieldValue } from './values';

/**
 * Evaluate one condition against the current form values. The values map is
 * keyed by field.id (the SOURCE field's id, not the gated target's). Returns
 * true when the condition is satisfied — i.e. the gated content should be
 * visible.
 *
 * Operators:
 *   equals       — string equality (after both sides are stringified)
 *   not_equals   — inverse of equals
 *   is_true      — truthy boolean / "true" string / numeric 1
 *   is_false     — opposite of is_true
 *   contains     — array source (CheckboxGroup) includes the target value
 *   not_contains — array source does NOT include the target value
 *   age_years_lt — Date source: computed age in whole years is < target
 *   age_years_ge — Date source: computed age in whole years is >= target
 *   count_eq     — Repeatable section source (fieldId = sectionId):
 *                  instance count == target
 *   count_ge     — Repeatable section source: instance count >= target
 *
 * **Unset semantics — operator-aware.** Positive-match operators (`equals`,
 * `contains`, `is_true`) treat an unset source as a non-match → hidden.
 * Negative-match operators (`not_equals`, `not_contains`, `is_false`) treat
 * an unset source as a match → visible. This matches the typical
 * conditional-form UX where dependent fields APPEAR as the advisor answers
 * upstream questions (rather than starting fully visible and shrinking),
 * AND keeps "show this unless they explicitly picked X" patterns working.
 *
 * Empty strings / arrays / NaN numbers all count as unset. An `instanceScope:
 * 'all'` condition over a repeatable with NO instances is unset by the same
 * rule — see the quantifier branch below for why `any` does not follow it.
 */
function evaluateOne(c: FileEntryCondition, values: Record<string, FieldValue>): boolean {
  // Count operators short-circuit before the standard value lookup — the
  // condition's `fieldId` is a sectionId (not a workflow field id), and
  // the "value" the runtime checks is the section's instance count rather
  // than a normal field slot. No floor is applied: a section the map says
  // nothing about reads 0, not the renderer's starter instance.
  if (c.operator === 'count_eq' || c.operator === 'count_ge') {
    // Reject a threshold that isn't a strict non-negative integer — a
    // stray character (`Number.parseInt('18abc')` returns 18) would
    // otherwise silently reinterpret a mis-authored threshold.
    if (!/^\d+$/.test(String(c.value ?? '').trim())) return false;
    const threshold = Number.parseInt(c.value, 10);
    if (!Number.isFinite(threshold)) return false;
    // Sentinel when there is one, otherwise the highest instance index the
    // section's own keys mention — NOT a flat 1 for "has keys but no sentinel",
    // which is exactly the shape every submit-time map has (the snapshot does
    // not persist the sentinel). Reading 1 there made `count_eq 2` unsatisfiable
    // on the server while it passed in the browser, and a role gated on it lost
    // its anchors from the generated PDF after BMO had already been told to look
    // for them. No sentinel AND no instance keys still reads 0, so a misauthored
    // condition (a field id where a section id belongs, or a repeatable flag
    // since cleared) still fails closed rather than matching `count_ge 1`.
    const count = instanceCountFromValues(values, c.fieldId);
    return c.operator === 'count_eq' ? count === threshold : count >= threshold;
  }
  // Instance-quantified source: the field lives inside a REPEATABLE section,
  // so there is no bare `values[fieldId]` to read — one value per instance
  // sits under `inst:<sectionId>:<idx>:<fieldId>`. Gather them and apply the
  // operator across the set.
  //
  // ZERO INSTANCES — the section is empty, or (far more often) it was gated
  // away on another step and never rendered at all. The quantifiers split here,
  // and the split is the whole point:
  //
  //   `any` is existential — false over an empty set, always. "At least one
  //   beneficiary is in BC" cannot be true with no beneficiaries.
  //
  //   `all` is universal, so it defers to the SAME operator-aware unset rule
  //   an unscoped condition uses (see {@link applyOperator}). Positive
  //   operators still read false: "every beneficiary is a BC resident" must
  //   not be vacuously true on a form with no beneficiaries, or the BC Grant
  //   question surfaces on an empty form. Negative operators read TRUE, which
  //   is both the honest vacuous reading and the only one consistent with the
  //   unscoped case — `not_equals` on an unanswered plain field is already a
  //   match.
  //
  // A flat `return false` here broke RDSP: the Primary Caregiver signer role is
  // gated on "no accountholder is already the PCG" (`not_equals yes`, scope
  // `all`) over a repeatable that only exists on the beneficiary-is-NOT-holder
  // branch. Pick "beneficiary is the accountholder" and the section never
  // renders, so the row that should read "correct, none of them did" instead
  // failed closed and silently dropped the caregiver from the signing package.
  if (c.instanceScope === 'any' || c.instanceScope === 'all') {
    const perInstance = collectInstanceValues(values, c.fieldId);
    if (perInstance.length === 0) {
      return c.instanceScope === 'all' ? applyOperator(c, undefined) : false;
    }
    return c.instanceScope === 'any'
      ? perInstance.some((v) => applyOperator(c, v))
      : perInstance.every((v) => applyOperator(c, v));
  }

  // One NAMED instance ("Accountholder 1 is the primary caregiver"). Neither quantifier can
  // express this: `any` is true whether holder 1, holder 2 or both answered yes, so it
  // cannot tell the combined "Accountholder 1 / Primary caregiver" signatory role apart
  // from the "Accountholder 2 / …" one.
  const index = instanceScopeIndex(c.instanceScope);
  if (index !== null) return applyOperator(c, instanceValueAt(values, c.fieldId, index));

  return applyOperator(c, values[c.fieldId]);
}

/**
 * Apply one condition's operator to a single concrete value. Split out of
 * {@link evaluateOne} so instance-quantified conditions can run the same
 * comparison once per repeatable instance.
 */
function applyOperator(c: FileEntryCondition, v: FieldValue): boolean {
  const isSet = isScalarValueSet(v);

  // Unset source → operator-aware default. Positive-match operators expect a
  // concrete value to match against, so unset means "hide." Negative-match
  // operators (and `is_false`) expect the absence of a value, so unset means
  // "still visible."
  if (!isSet) {
    return (
      c.operator === 'not_equals' || c.operator === 'not_contains' || c.operator === 'is_false'
    );
  }

  switch (c.operator) {
    case 'is_true':
      return v === true || v === 'true' || v === 1;
    case 'is_false':
      return v === false || v === 'false' || v === 0;
    case 'equals':
      // SingleSelect / RadioGroup store the option's `value` string; we
      // compare on string identity so a numeric option still works.
      return String(v) === c.value;
    case 'not_equals':
      return String(v) !== c.value;
    case 'contains':
      // CheckboxGroup holds an array of option values. Stringify each entry
      // so a numeric option still matches a string-typed condition value.
      return Array.isArray(v) && v.some((item) => String(item) === c.value);
    case 'not_contains':
      return !Array.isArray(v) || !v.some((item) => String(item) === c.value);
    case 'age_years_lt':
    case 'age_years_ge': {
      // Source is a Date value stored as an ISO date string (YYYY-MM-DD).
      // Compute whole-year age at "today" — anything unparseable fails
      // closed (hidden) so a corrupted DOB doesn't accidentally reveal
      // sensitive downstream steps.
      const years = ageInWholeYears(v);
      if (years === null) return false;
      // Match the count-operator strict-integer parse — a stray suffix
      // on the threshold should fail closed, not silently truncate to a
      // valid-looking number via Number.parseInt('18abc').
      if (!/^\d+$/.test(String(c.value ?? '').trim())) return false;
      const threshold = Number.parseInt(c.value, 10);
      if (!Number.isFinite(threshold)) return false;
      return c.operator === 'age_years_lt' ? years < threshold : years >= threshold;
    }
    default:
      // Unknown operator — fail open (treat as visible) so an over-eager BE
      // future addition doesn't silently hide every gated field on this FE.
      return true;
  }
}

/**
 * Convert a stored Date value into whole years since today. Handles the
 * shape the runtime persists — an ISO 8601 date string emitted by the FE
 * Date picker's `dayjs(v).format('YYYY-MM-DD')`. Returns null when the
 * input isn't a parseable date, so the caller can treat unparseable DOBs
 * as "hidden" for positive-match operators.
 *
 * Delegates to dayjs to match the picker's timezone semantics — avoids
 * the `new Date('YYYY-MM-DD')` trap where the string parses as UTC
 * midnight but `.getFullYear()`/`.getMonth()`/`.getDate()` read locally,
 * shifting the birthday one day earlier in negative-UTC zones. On
 * 2026-05-14 in Toronto (UTC-4), DOB '2000-05-15' produced 26 years via
 * the naive Date math even though the real age is 25.
 */
/**
 * True when a raw field value counts as "answered". Empty strings, NaN
 * numbers, and empty arrays all read as unset — the same rule the
 * condition evaluator applies before falling back to its operator-aware
 * unset default. Shared so the preview evaluator's "has the originator
 * answered this upstream field yet?" check can't drift from it.
 */
function isScalarValueSet(v: FieldValue): boolean {
  return (
    v !== null &&
    v !== undefined &&
    (typeof v !== 'string' || v.trim().length > 0) &&
    (typeof v !== 'number' || Number.isFinite(v)) &&
    (!Array.isArray(v) || v.length > 0)
  );
}

/**
 * True when a condition's SOURCE has a concrete value in `values` — i.e. the
 * user has actually answered the field the condition keys off. Count
 * operators key off the section's instance sentinel / instance keys rather
 * than a plain field slot, so they get their own liveness check.
 */
function isConditionSourceSet(c: FileEntryCondition, values: Record<string, FieldValue>): boolean {
  if (c.operator === 'count_eq' || c.operator === 'count_ge') {
    const raw = values[`__instanceCount:${c.fieldId}`];
    return (
      (typeof raw === 'number' && Number.isFinite(raw)) || hasAnyInstanceKey(values, c.fieldId)
    );
  }
  if (c.instanceScope === 'any' || c.instanceScope === 'all') {
    return collectInstanceValues(values, c.fieldId).some(isScalarValueSet);
  }
  // An indexed scope reads one instance key, so the bare slot below would always report
  // "unanswered" and hand every such condition the cross-step excuse for free.
  const index = instanceScopeIndex(c.instanceScope);
  if (index !== null) return isScalarValueSet(instanceValueAt(values, c.fieldId, index));
  return isScalarValueSet(values[c.fieldId]);
}

/**
 * Every repeatable instance's value for one authored field id, in instance
 * order.
 *
 * The evaluator only ever receives the flat values map, not the template, so
 * the owning section is recovered from the key shape
 * `inst:<sectionId>:<idx>:<fieldId>`. Field ids are UUIDs, so a suffix match
 * can't collide with another field.
 *
 * The instance COUNT deliberately comes from the section, not from the keys
 * this particular field happens to have: a beneficiary who filled in their
 * date of birth but not their province still exists, and must contribute an
 * `undefined` so an `all` quantifier doesn't read as satisfied. Counting only
 * the field's own keys silently skipped them.
 */
function instanceSectionIdOf(values: Record<string, FieldValue>, fieldId: string): string | null {
  const suffix = `:${fieldId}`;
  for (const key of Object.keys(values)) {
    if (!key.startsWith('inst:') || !key.endsWith(suffix)) continue;
    const parts = key.split(':');
    // inst : sectionId : idx : fieldId — section + field ids are UUIDs, so
    // a well-formed key always splits into exactly four segments.
    if (parts.length !== 4) continue;
    const sectionId = parts[1] ?? null;
    if (sectionId) return sectionId;
  }
  return null;
}

function collectInstanceValues(values: Record<string, FieldValue>, fieldId: string): FieldValue[] {
  const sectionId = instanceSectionIdOf(values, fieldId);
  if (sectionId === null) return [];

  const count = instanceCountFromValues(values, sectionId);
  const out: FieldValue[] = [];
  for (let i = 0; i < count; i++) out.push(values[`inst:${sectionId}:${i}:${fieldId}`]);
  return out;
}

/**
 * The value one NAMED instance of a repeatable section holds for `fieldId`, or `undefined`
 * when that instance does not exist.
 *
 * Bounded by the live instance count, deliberately. Removing an instance can leave stale
 * `inst:` keys behind, and an unbounded read would let a holder the advisor has already
 * deleted go on satisfying a gate — the one failure mode an index has that `any` / `all`
 * do not, since both of those are bounded by the same count.
 *
 * Out of range returns `undefined` rather than failing closed, so {@link applyOperator}
 * applies its operator-aware unset rule: "holder 2 is the primary caregiver" is false on a
 * one-holder application, and "holder 2 is NOT" is true. That is the same reading `all`
 * takes over zero instances, and the two must agree — a role gated "holder 2 is not the
 * PCG" has to survive a form where holder 2 was never added.
 */
function instanceValueAt(
  values: Record<string, FieldValue>,
  fieldId: string,
  index: number,
): FieldValue {
  const sectionId = instanceSectionIdOf(values, fieldId);
  if (sectionId === null) return undefined;
  if (index >= instanceCountFromValues(values, sectionId)) return undefined;
  return values[`inst:${sectionId}:${index}:${fieldId}`];
}

/**
 * The 0-based instance index an `instanceScope` names, or null when it is a quantifier
 * (`any` / `all`), absent, or malformed.
 *
 * Validated with the same strict decimal test `count_eq` / `count_ge` use on their
 * threshold: `Number.parseInt('1abc')` returns 1, so a mis-authored scope would otherwise
 * silently become a valid index rather than being ignored.
 *
 * Exported because a FILE ENTRY carries the same vocabulary on its own `instanceScope` (which
 * instance its filename tokens read), and the two must agree on what a valid scope is — a gate
 * that reads holder 2 beside a filename that reads holder 1 is the bug this shape prevents.
 */
export function instanceScopeIndex(scope: string | null | undefined): number | null {
  if (typeof scope !== 'string' || !/^\d+$/.test(scope.trim())) return null;
  const index = Number.parseInt(scope.trim(), 10);
  return Number.isSafeInteger(index) ? index : null;
}

/**
 * True when the values map has ANY `inst:<sectionId>:*` key — i.e. the section
 * has been populated, whatever its count says.
 *
 * Only {@link isConditionSourceSet} needs this now. The count OPERATORS read
 * {@link instanceCountFromValues} instead, which answers the stronger question
 * (how many) from the same keys; liveness is the weaker one, and a section
 * sentinelled to 0 is still a live source that has simply been emptied.
 */
function hasAnyInstanceKey(values: Record<string, FieldValue>, sectionId: string): boolean {
  const prefix = `inst:${sectionId}:`;
  for (const k of Object.keys(values)) {
    if (k.startsWith(prefix)) return true;
  }
  return false;
}

function ageInWholeYears(v: unknown): number | null {
  if (typeof v !== 'string' || v.length === 0) return null;
  const parsed = dayjs(v);
  if (!parsed.isValid()) return null;
  const years = dayjs().diff(parsed, 'year');
  return Number.isFinite(years) ? years : null;
}

/**
 * Combine a rule's conditions, given a per-condition verdict.
 *
 * A rule is `ALL(and-group) && ANY(or-group)`. Each condition picks its own
 * group through `connector`; one that doesn't inherits the list's combinator,
 * which is what makes every rule authored before `connector` existed evaluate
 * exactly as it always did — `'all'` sends every row to the and-group (`every`),
 * `'any'` sends every row to the or-group (`some`).
 *
 * An EMPTY or-group is "no such requirement", not "nothing matched": a pure-AND
 * rule must not be false just because it has no OR rows. The and-group needs no
 * such carve-out — `every` over nothing is already true.
 *
 * Takes the verdict as a parameter so the plain and preview evaluators share one
 * grouping. The preview's cross-step excuse has to apply per CONDITION, before
 * grouping, exactly as it applied before the combinator — and the only way to
 * guarantee the two can't drift is for there to be one implementation.
 */
function combine(
  conditions: FileEntryCondition[],
  combinator: 'all' | 'any' | null | undefined,
  verdict: (c: FileEntryCondition) => boolean,
): boolean {
  // Default to "all" — matches the domain default when the BE rejected an
  // unspecified combinator (we never persist non-empty conditions without
  // one, but the fallback keeps the renderer safe).
  const fallback = combinator === 'any' ? 'or' : 'and';
  const ands: FileEntryCondition[] = [];
  const ors: FileEntryCondition[] = [];
  for (const c of conditions) ((c.connector ?? fallback) === 'or' ? ors : ands).push(c);

  if (!ands.every(verdict)) return false;
  return ors.length === 0 || ors.some(verdict);
}

/**
 * Evaluate a list of conditions against the form values. Empty conditions list
 * is treated as "always visible". See {@link combine} for how the two groups
 * compose.
 */
export function evaluateConditions(
  conditions: FileEntryCondition[] | undefined | null,
  combinator: 'all' | 'any' | null | undefined,
  values: Record<string, FieldValue>,
): boolean {
  if (!conditions || conditions.length === 0) return true;
  return combine(conditions, combinator, (c) => evaluateOne(c, values));
}

/**
 * Preview-mode visibility for a SECTION. Mirrors {@link evaluateConditions}
 * with one carve-out: a CROSS-STEP condition (source lives on a step other
 * than `ownStepId`) whose source is still UNSET is treated as satisfied.
 *
 * Rationale: the originator preview must not hide a downstream section merely
 * because an upstream branch selector hasn't been answered yet — a gated tab
 * would otherwise present as a blank page. But once the originator DOES answer
 * that upstream field, the branch should be honored (a section gated
 * `Beneficiary Type is_not RESP` must disappear once RESP is picked). Same-step
 * conditions are never excused — those are inline reveal patterns that are
 * meant to start hidden.
 *
 * The excuse is applied per-condition BEFORE the grouping, so a rule with a mix
 * of answered and unanswered cross-step sources still composes correctly — and
 * so an excused row lands in its own group rather than short-circuiting the
 * whole rule.
 */
export function evaluateConditionsForPreview(
  conditions: FileEntryCondition[] | undefined | null,
  combinator: 'all' | 'any' | null | undefined,
  values: Record<string, FieldValue>,
  ownStepId: string,
): boolean {
  if (!conditions || conditions.length === 0) return true;
  const satisfiedOrExcused = (c: FileEntryCondition): boolean =>
    evaluateOne(c, values) ||
    // Cross-step AND not yet answered → excuse it (don't let an unanswered
    // upstream field blank out the preview tab).
    (c.fieldStepId !== ownStepId && !isConditionSourceSet(c, values));
  return combine(conditions, combinator, satisfiedOrExcused);
}

/**
 * The "Disable if" rule pair, carried by every element that supports it —
 * WorkflowStep, FormSection and WorkflowField. Structural rather than imported
 * from templateTypes so this module stays dependency-free in that direction.
 */
export type DisableGated = {
  disableConditions?: FileEntryCondition[] | null;
  disableConditionCombinator?: 'all' | 'any' | null;
};

/**
 * "Disable if" — the INVERSE polarity of {@link evaluateConditions}: a MATCH
 * makes the element read-only. Unlike a failed visibility rule, the element
 * stays rendered, stays navigable, and its answer still reaches the PDF and
 * BMO (see `visibleValues.ts`, which reads `conditions` and never this list).
 *
 * This is the ONLY place that inversion lives. Never write
 * `!evaluateConditions(x.disableConditions, …)` at a call site: that expression
 * is WRONG for the empty list, because `evaluateConditions([])` is `true`
 * ("always visible"), so negating it would disable every element that has no
 * rule at all. The early return below is the whole safety of the feature.
 *
 * Deliberately has no `…ForPreview` variant. That carve-out excuses an
 * unanswered cross-step source so a gated preview tab isn't blank; applied to a
 * disable rule the excuse runs backwards and would render elements inert by
 * default in the originator's preview. Every mode uses the plain evaluator.
 */
export function isDisabledByRule(
  element: DisableGated | null | undefined,
  values: Record<string, FieldValue>,
): boolean {
  const list = element?.disableConditions;
  if (!list || list.length === 0) return false;
  return evaluateConditions(list, element.disableConditionCombinator, values);
}
